import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV } from "@/data/site";
import { useModal } from "./Forms";

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const { openInstitution, openStudent } = useModal();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (item) => {
    setOpenMenu(false);
    if (item.route) {
      navigate(item.href);
    } else if (window.location.pathname !== "/") {
      navigate("/" + item.href);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "border-b border-white/10 bg-ink-0/80 backdrop-blur-xl" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5" data-testid="logo">
          <img src="/a3-logo-mark.png" alt="A3 Global logo" className="h-9 w-auto" />
          <span className="flex flex-col leading-none">
            <span className="font-display text-lg font-bold tracking-tight text-white">
              A3 <span className="text-gradient">GLOBAL</span>
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.28em] text-white/45">
              Marketing &amp; Education
            </span>
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          {NAV.map((item) =>
            item.route ? (
              <Link
                key={item.label}
                to={item.href}
                className="rounded-full px-3.5 py-2 text-sm text-white/70 transition-colors hover:text-brand-cyan"
                data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {item.label}
              </Link>
            ) : (
              <a
                key={item.label}
                href={window.location.pathname === "/" ? item.href : "/" + item.href}
                className="rounded-full px-3.5 py-2 text-sm text-white/70 transition-colors hover:text-brand-cyan"
                data-testid={`nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {item.label}
              </a>
            )
          )}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <button
            onClick={openStudent}
            className="rounded-full border border-white/15 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:border-brand-cyan/50 hover:text-brand-cyan"
            data-testid="nav-find-college"
          >
            Find Your Dream College
          </button>
          <button
            onClick={openInstitution}
            className="rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-5 py-2.5 text-sm font-medium text-[#08120A] transition-shadow hover:shadow-[0_0_22px_rgba(198,255,58,0.45)]"
            data-testid="nav-partner"
          >
            Partner With A3
          </button>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpenMenu((o) => !o)} data-testid="mobile-menu-toggle">
          {openMenu ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {openMenu && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-white/10 bg-ink-0/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {NAV.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNav(item)}
                  className="rounded-lg px-3 py-2.5 text-left text-sm text-white/80 hover:bg-white/5"
                  data-testid={`mobile-nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {item.label}
                </button>
              ))}
              <div className="mt-3 flex flex-col gap-2">
                <button onClick={() => { setOpenMenu(false); openStudent(); }} className="rounded-full border border-white/15 px-5 py-3 text-sm font-medium text-white" data-testid="mobile-nav-find-college">
                  Find Your Dream College
                </button>
                <button onClick={() => { setOpenMenu(false); openInstitution(); }} className="rounded-full bg-gradient-to-r from-brand-blue to-brand-cyan px-5 py-3 text-sm font-medium text-[#08120A]" data-testid="mobile-nav-partner">
                  Partner With A3
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
